package Utils

import DataBase.UINKDBInterface
import Structs.Product
import Structs.TaxNoteExplain
import Structs.TaxNoteRowDataType
import Structs.User
import kotlin.math.abs

/** User.print_state: category header (not collapsed). */
const val PRINT_STATE_GROUP_CATEGORY = 31
const val PRINT_STATE_GROUP_CATEGORY2 = 32

/** User.print_state: collapse same-group products into one line with average price. */
const val PRINT_STATE_MERGE_CATEGORY = 33
const val PRINT_STATE_MERGE_CATEGORY2 = 34

/**
 * User.print_state: product photo column on order, delivery, and tax PDFs.
 * Photos are downloaded only when this state is set. -40 is the same column
 * without the print_state >= 30 table color.
 */
const val PRINT_STATE_PRODUCT_IMAGES = 40

fun printStateMergesProductLines(printState: Int): Boolean =
    printState == PRINT_STATE_MERGE_CATEGORY || printState == PRINT_STATE_MERGE_CATEGORY2

fun printStateShowsProductImages(printState: Int): Boolean =
    printState == PRINT_STATE_PRODUCT_IMAGES || printState == -PRINT_STATE_PRODUCT_IMAGES

/**
 * Invoice/delivery line collapse by catalog `notes3`.
 * Trigger: user default_notes entry `pnotes3` with [ReadUserCommentJson.forceInNote] == 2.
 */
fun User?.mergePrintByPnotes3(): Boolean =
    this?.getJsonDesc("pnotes3")?.forceInNote == 2

/**
 * Stable group key for catalog pnotes3.
 * Empty/blank notes3 uses `id:{productId}` so the product is not mixed with others.
 * Those rows stay as separate lines; only a shared notes3 label is collapsed.
 */
fun Product.pnotes3PrintGroupKey(): String =
    notes3?.trim()?.takeIf { it.isNotEmpty() } ?: "id:$id"

fun Product.pnotes3PrintGroupName(date: String): String =
    notes3?.trim()?.takeIf { it.isNotEmpty() } ?: getName(date)

fun Product.printLineGroupKey(printState: Int, date: String): String {
    return when (printState) {
        PRINT_STATE_GROUP_CATEGORY, PRINT_STATE_MERGE_CATEGORY -> getCategory(date)
        PRINT_STATE_GROUP_CATEGORY2, PRINT_STATE_MERGE_CATEGORY2 -> getCategory2(date)
        else -> ""
    }
}

fun Product.printLineGroupName(printState: Int, date: String): String {
    return when (printState) {
        PRINT_STATE_GROUP_CATEGORY, PRINT_STATE_MERGE_CATEGORY -> getCategory(date)
        PRINT_STATE_GROUP_CATEGORY2, PRINT_STATE_MERGE_CATEGORY2 -> getCategory2(date)
        else -> getName(date)
    }
}

private val taxProductRowTypes = setOf(
    TaxNoteRowDataType.PRODUCT,
    TaxNoteRowDataType.PRODUCT_SINGLE,
    TaxNoteRowDataType.PRODUCT_SINGLE_NOTE,
)

/**
 * Collapse invoice product rows when default_notes pnotes3.forceInNote == 2.
 * Non-product rows (hand/free/notes/sum) keep their place; appendix paths should skip this.
 */
fun mergeTaxExplainsForPrint(
    rows: List<TaxNoteExplain>,
    mergeByPnotes3: Boolean,
    date: String,
    isClient: Boolean,
): List<TaxNoteExplain> {
    if (!mergeByPnotes3) return rows
    val db = UINKDBInterface.activeDB
    fun productOf(id: Int) =
        if (isClient) db.getClientProduct(id).first else db.getSupplierProduct(id).first

    val groups = linkedMapOf<String, MutableList<TaxNoteExplain>>()
    val firstIndexOfGroup = linkedMapOf<String, Int>()
    rows.forEachIndexed { index, row ->
        val pid = row.product_id
        if (row.type in taxProductRowTypes && pid != null) {
            val key = productOf(pid)?.pnotes3PrintGroupKey() ?: "id:$pid"
            // Blank notes3 is `id:{productId}` so the product is not mixed with others.
            // Keep each source row; only a real notes3 label collapses lines.
            if (key.startsWith("id:")) return@forEachIndexed
            if (key !in firstIndexOfGroup) firstIndexOfGroup[key] = index
            groups.getOrPut(key) { mutableListOf() }.add(row)
        }
    }
    if (groups.isEmpty()) return rows

    val groupKeyAtFirstIndex = firstIndexOfGroup.entries.associate { it.value to it.key }
    val result = ArrayList<TaxNoteExplain>(rows.size)
    rows.forEachIndexed { index, row ->
        val groupKey = groupKeyAtFirstIndex[index]
        if (groupKey != null) {
            val group = groups[groupKey]!!
            val pid = group.first().product_id
            val name = pid?.let { productOf(it)?.pnotes3PrintGroupName(date) }
                ?: group.first().describe
            result.add(mergeGroupedTaxExplains(group, name))
            return@forEachIndexed
        }
        val pid = row.product_id
        val rowKey = if (row.type in taxProductRowTypes && pid != null)
            productOf(pid)?.pnotes3PrintGroupKey() ?: "id:$pid"
        else null
        val groupedProduct = rowKey != null && !rowKey.startsWith("id:")
        if (!groupedProduct) result.add(row)
    }
    return result
}

internal fun mergeGroupedTaxExplains(group: List<TaxNoteExplain>, name: String): TaxNoteExplain {
    val first = group.first()
    if (group.size == 1) {
        return if (first.describe == name) first else first.copy(describe = name)
    }
    val amount = group.sumOf { it.amount.toDouble() }.toFloat()
    val returns = group.sumOf { it.returns.toDouble() }.toFloat()
    val totalBefore = group.sumOf { it.total_before.toDouble() }.toFloat()
    val totalAfter = group.sumOf { it.total_after.toDouble() }.toFloat()
    val taxPaid = group.sumOf { it.tax_paid.toDouble() }.toFloat()
    val qty = amount - returns
    val priceBefore = if (abs(qty) > 0.0001f) totalBefore / qty else 0f
    val priceAfter = if (abs(qty) > 0.0001f) totalAfter / qty else 0f
    val amountSecond = group.sumOf { (it.amountSecond ?: 0f).toDouble() }.toFloat()
    val amountSecondReturn = group.sumOf { (it.amountSecondReturn ?: 0f).toDouble() }.toFloat()
    val amountThird = group.sumOf { (it.amountThird ?: 0f).toDouble() }.toFloat()
    val thirdName = group.mapNotNull { it.amountThirdUnitName?.takeIf { n -> n.isNotEmpty() } }
        .distinct().singleOrNull()
    return TaxNoteExplain(
        type = TaxNoteRowDataType.PRODUCT,
        describe = name,
        barcode = "",
        date = first.date,
        amount = amount,
        returns = returns,
        price_before = priceBefore,
        price_after = priceAfter,
        discount = 0f,
        total_before = totalBefore,
        total_after = totalAfter,
        tax_paid = taxPaid,
        price_min = priceAfter,
        price_max = priceAfter,
        price_min_before = priceBefore,
        price_max_before = priceBefore,
        product_id = first.product_id,
        amountSecond = amountSecond.takeIf { it != 0f },
        amountSecondReturn = amountSecondReturn.takeIf { it != 0f },
        amountThird = amountThird.takeIf { it != 0f },
        amountThirdUnitName = thirdName,
    )
}
